import CustomDatePicker from 'Components/CustomDatePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import MultiSelect from 'Components/MultiSelect/MultiSelect'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import ReportDownload from 'Services/SalesManagement/Features/Report/Components/ReportDownload'
import ReportProgress from 'Services/SalesManagement/Features/Report/Components/ReportProgress'
import { useGetAffiliateOrderReportFilterOptionsQuery } from 'Services/SalesManagement/Features/Report/reportApi'
import {
  AFFILIATE_ORDER_REPORT_PREFIX,
  type AffiliateOrderReportParamShape,
  createAffiliateOrderReport,
  fetchAffiliateOrderReport,
  ReportGenerationStatus,
  resetAffiliateOrderReportWebsocket
} from 'Services/SalesManagement/Features/Report/reportSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import GenerateReportIcon from '@mui/icons-material/CloudUpload'
import { FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { type FormEvent, useState } from 'react'
import { t } from 'ttag'

export default function AffiliateOrderReport() {
  const dispatch = useAppDispatch()
  const {
    affiliateOrderReportLoading,
    singleAffiliateOrderReport,
    affiliateReportGenerationStatus,
    affiliateReportGenerationTask
  } = useTypedSelector(state => state.report)

  const {
    data: affiliateOrderReportFilterOptions,
    isFetching: isAffiliateOrderReportFilterOptionsFetching
  } = useGetAffiliateOrderReportFilterOptionsQuery()

  const [params, setParams] = useState<AffiliateOrderReportParamShape>({
    operators: [],
    customer_group: '',
    payment_method: '',
    product_id: '',
    status: '',
    from_date: '',
    to_date: ''
  })

  const handleReportGenerate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Reset the report generation state before starting a new one
    dispatch(resetAffiliateOrderReportWebsocket())
    const reportParams: AffiliateOrderReportParamShape = { ...params }
    if (!reportParams.from_date) delete reportParams.from_date
    if (!reportParams.to_date) delete reportParams.to_date
    dispatch(createAffiliateOrderReport(reportParams))
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Report generation successfully started.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error starting the report generation.`)
      })
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setParams(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const handleMultiSelectChange = (
    selected: string[],
    name: keyof AffiliateOrderReportParamShape
  ) => {
    setParams(prevState => ({ ...prevState, [name]: selected }))
  }

  const handleSelectChange = (
    event: SelectChangeEvent,
    name: keyof AffiliateOrderReportParamShape
  ) => {
    const { value } = event.target
    setParams(prevState => ({ ...prevState, [name]: value }))
  }

  const isLoading = affiliateOrderReportLoading === 'pending'
  const isReportGenerationDisabled =
    affiliateReportGenerationStatus?.status === ReportGenerationStatus.PENDING ||
    affiliateReportGenerationStatus?.status === ReportGenerationStatus.PROGRESS

  return (
    <WithLoader loading={isAffiliateOrderReportFilterOptionsFetching}>
      <Paper sx={{ width: '100%', my: 2, p: 2, pt: 1 }}>
        <StyledForm onSubmit={handleReportGenerate} isDisabled={isLoading}>
          <Box sx={{ minWidth: 200, maxWidth: 400 }}>
            <MultiSelect
              handleChange={selected => handleMultiSelectChange(selected, 'operators')}
              label={t`Operator`}
              required={true}
              options={affiliateOrderReportFilterOptions?.field_choices.operators ?? []}
            />
          </Box>
          <Box>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t`Payment method`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={params.payment_method}
                label={t`Payment method`}
                autoWidth
                onChange={e => handleSelectChange(e, 'payment_method')}
              >
                {affiliateOrderReportFilterOptions?.field_choices.payment_method.map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t`Customer group`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={params.customer_group}
                label={t`Customer group`}
                autoWidth
                onChange={e => handleSelectChange(e, 'customer_group')}
              >
                {affiliateOrderReportFilterOptions?.field_choices.customer_group.map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t`Status`}</InputLabel>
              <Select
                sx={{ backgroundColor: 'background.default' }}
                value={params.status}
                label={t`Status`}
                autoWidth
                onChange={e => handleSelectChange(e, 'status')}
              >
                {affiliateOrderReportFilterOptions?.field_choices.status.map(option => (
                  <MenuItem key={option[0]} value={option[0]}>
                    {option[1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="from_date"
                value={params.from_date ? new Date(params.from_date) : null}
                dateToCompare={params.to_date ? new Date(params.to_date) : null}
                dateToCompareFieldName="to_date"
                isStartDate={true}
                label={t`Start date`}
              />
              <Box>{` - `}</Box>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="to_date"
                value={params.to_date ? new Date(params.to_date) : null}
                dateToCompare={params.from_date ? new Date(params.from_date) : null}
                dateToCompareFieldName="from_date"
                isStartDate={false}
                label={t`End date`}
              />
            </Box>
          </Box>
          <Box>
            <CustomLoadingButton
              isLoading={isLoading}
              type="submit"
              disabled={isReportGenerationDisabled}
              icon={<GenerateReportIcon />}
              iconPosition="start"
            >{t`Generate report`}</CustomLoadingButton>
          </Box>
          <Box sx={{ my: 4 }}>
            <ReportProgress
              reportGenerationTask={affiliateReportGenerationTask}
              reportPrefix={AFFILIATE_ORDER_REPORT_PREFIX}
              reportStatus={affiliateReportGenerationStatus}
              reportPath="reports/affiliate-orders/sales"
              resetReportWebsocketState={() => {
                dispatch(resetAffiliateOrderReportWebsocket())
              }}
            />
          </Box>
        </StyledForm>
        <ReportDownload
          singleReport={singleAffiliateOrderReport}
          reportGenerationStatus={affiliateReportGenerationStatus}
          fetchReport={reportName =>
            dispatch(fetchAffiliateOrderReport({ report_name: reportName, ...params }))
          }
          isLoading={isLoading}
        />
      </Paper>
    </WithLoader>
  )
}
